import { Box } from '@mui/material';
import { Stack } from '@mui/system';
import ProductsDrawer from 'components/drawers/products-drawer/products-drawer.component';
import FormSection from 'components/form-section/form-section.component';
import { FormFieldPresence } from 'components/motion/motion.component';
import { AnimatePresence } from 'framer-motion';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';
import DrawerField from '../fields/drawer-field/drawer-field.component';
import IncSwitchField from '../fields/inc-switch-field/inc-switch-field.component';
import IncTextField from '../fields/inc-text-field/inc-text-field.component';
import SchedulerField from '../fields/scheduler-field/scheduler-field.component';
import { useSchedulerField } from '../fields/scheduler-field/scheduler-field.hooks';
import ToggleButtonGroupField from '../fields/toggle-button-group-field/toggle-button-group-field.component';
import NumericStepInputField from '../fields/numeric-step-input-field/numeric-step-input-field.component';

const makeName = (name: string, prefix: string) =>
  prefix ? `${prefix}.${name}` : name;

const LoyaltySpecForm = ({ prefix = '' }: { prefix?: string }) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();

  const existingProducts = useWatch({
    name: makeName('excludedProducts', prefix),
  });
  const expiryMode = useWatch({ name: makeName('expiryMode', prefix) });
  const dynamicPointsAwardRatioEnabled = useWatch({
    name: makeName('dynamicPointsAwardRatioEnabled', prefix),
  });

  const schedulerField = useSchedulerField({
    name: makeName('dynamicPointsAwardRatio', prefix),
    additionalData: {
      additionalDataField: (
        <NumericStepInputField
          name="additionalData"
          label={t(
            'page.settings.loyalty-specs.points.dynamic.dynamicPointsAwardRatio.label',
          )}
          step={1}
          allowNegative={false}
          decrementDisabled={value => value < 1}
          sx={{ my: 0 }}
        />
      ),
      additionalDataSchema: object({
        additionalData: number().required().min(0).default(0),
      }),
      additionalDataRenderer: ({ row }) => row.additionalData,
      additionalDataColumnLabel: t(
        'page.settings.loyalty-specs.points.dynamic.dynamicPointsAwardRatio.columnLabel',
      ),
    },
    compact: true,
  });

  const expirationTypes = [
    {
      label: t('page.settings.loyalty-specs.points.expiration.neverExpire'),
      value: 'NoExpiry',
      test: 'LoyaltySpecForm.IncTextField.expiryMode.NeverExpire',
    },
    {
      label: t('page.settings.loyalty-specs.points.expiration.sinceDayEarning'),
      value: 'SinceCurrentTx',
      test: 'LoyaltySpecForm.IncTextField.expiryMode.SinceCurrentTx',
    },
  ];

  return (
    <FormSection title={t('page.settings.loyalty-specs.points.title')}>
      <IncTextField
        label={t('page.settings.loyalty-specs.points.ratio.label')}
        name={makeName('defaultPointsAwardRatio', prefix)}
        helperText={t('page.settings.loyalty-specs.points.ratio.helperText')}
        data-testid="LoyaltySpecForm.IncTextField.defaultPointsAwardRatio"
        margin="none"
      />

      <IncTextField
        label={t('page.settings.loyalty-specs.points.confirmationPeriod.label')}
        name={makeName('confirmationPeriod', prefix)}
        helperText={t(
          'page.settings.loyalty-specs.points.confirmationPeriod.helperText',
        )}
        data-testid="LoyaltySpecForm.IncTextField.confirmationPeriod"
      />

      <ToggleButtonGroupField
        label={t('page.settings.loyalty-specs.points.expiration.label')}
        name={makeName('expiryMode', prefix)}
        options={expirationTypes}
        exclusive
      />

      <AnimatePresence>
        {expiryMode === 'SinceCurrentTx' && (
          <FormFieldPresence>
            <IncTextField
              label={t('page.settings.loyalty-specs.points.expiryPeriod.label')}
              helperText={t(
                'page.settings.loyalty-specs.points.expiryPeriod.helperText',
              )}
              name={makeName('expiryPeriod', prefix)}
              data-testid="LoyaltySpecForm.IncTextField.expiryPeriod"
              fullWidth={false}
            />
          </FormFieldPresence>
        )}
      </AnimatePresence>

      <IncSwitchField
        name={makeName('awardPointsAsPending', prefix)}
        label={t('page.settings.loyalty-specs.points.pending.label')}
        helperText={t('page.settings.loyalty-specs.points.pending.helperText')}
        data-testid="LoyaltySpecForm.IncSwitchField.awardPointsAsPending"
      />

      <IncSwitchField
        name={makeName('awardPointsForGiftCardTransaction', prefix)}
        label={t('page.settings.loyalty-specs.points.giftCardPurchases.label')}
        helperText={t(
          'page.settings.loyalty-specs.points.giftCardPurchases.helperText',
        )}
        data-testid="LoyaltySpecForm.IncSwitchField.awardPointsForGiftCardTransaction"
      />

      <IncSwitchField
        name={makeName('dynamicPointsAwardRatioEnabled', prefix)}
        label={t('page.settings.loyalty-specs.points.dynamic.label')}
        helperText={
          <Stack direction={'column'} spacing={2}>
            <Box>
              {t('page.settings.loyalty-specs.points.dynamic.helperText')}
            </Box>
            <Box>
              {t(
                'page.settings.loyalty-specs.points.dynamic.helperTextDescription',
              )}
            </Box>
          </Stack>
        }
        data-testid="LoyaltySpecForm.IncSwitchField.dynamicPointsAwardRatioEnabled"
      />

      <AnimatePresence initial={false}>
        {dynamicPointsAwardRatioEnabled && (
          <FormFieldPresence>
            <SchedulerField value={schedulerField} />
          </FormFieldPresence>
        )}
      </AnimatePresence>

      <DrawerField
        name={makeName('excludedProducts', prefix)}
        label={t('page.settings.loyalty-specs.points.excluded.label')}
        helperText={t('page.settings.loyalty-specs.points.excluded.helperText')}
        hasValue={value => value?.length > 0}
        withValue={value =>
          t('page.settings.loyalty-specs.points.excluded.withValue', {
            length: value.length,
          })
        }
        withoutValue={(_value, _drawerProps) =>
          t('page.settings.loyalty-specs.points.excluded.withoutValue')
        }
        drawer={drawerProps => (
          <ProductsDrawer
            {...drawerProps}
            onValid={({ products }) =>
              setValue(makeName('excludedProducts', prefix), products)
            }
            existingProducts={existingProducts}
          />
        )}
      />
    </FormSection>
  );
};

export default LoyaltySpecForm;
